/**
 * Creates a shallow copy of `object` where properties described by `keys` are
 * replaced with values from `newObject`.
 *
 * @param object Source object.
 * @param newObject Object containing the replacement values for the properties described by `keys`.
 * @param keys Key or tuple of keys describing the nested path to replace.
 */
export const setObjectProperty = <
  Object extends Record<string, any>,
  PropertyKey1 extends keyof Object,
  PropertyKey2 extends keyof Object[PropertyKey1],
  PropertyKey3 extends keyof Object[PropertyKey1][PropertyKey2],
>(
  object: Object,
  newObject: Object,
  propertyKeys:
    | PropertyKey1
    | [PropertyKey1, PropertyKey2]
    | [PropertyKey1, PropertyKey2, PropertyKey3],
): Object => {
  if (!Array.isArray(propertyKeys)) {
    return { ...object, [propertyKeys]: newObject[propertyKeys] }
  } else if (propertyKeys.length === 2) {
    return {
      ...object,
      [propertyKeys[0]]: {
        ...object[propertyKeys[0]],
        [propertyKeys[1]]: newObject[propertyKeys[0]][propertyKeys[1]],
      },
    }
  } else if (propertyKeys.length === 3) {
    return {
      ...object,
      [propertyKeys[0]]: {
        ...object[propertyKeys[0]],
        [propertyKeys[1]]: {
          ...object[propertyKeys[0]][propertyKeys[1]],
          [propertyKeys[2]]:
            newObject[propertyKeys[0]][propertyKeys[1]][propertyKeys[2]],
        },
      },
    }
  }
  throw new Error('Invalid keys length')
}
